<template>
  <div
    class="shadow-elevation-1 rounded-2xl mx-5 p-4 mt-4"
    :class="isLightMode ? 'bg-white text-[#21201E]' : 'bg-[#2B2926] text-white'"
  >
    <div
      v-if="hasHeader"
      class="flex items-start justify-between gap-4"
      :class="{ 'mb-4': bodyBlocks.length }"
    >
      <div
        v-if="title || resolveIcon(titleIcon, titleIconKey)"
        class="flex items-center gap-2 min-w-0"
      >
        <img
          v-if="resolveIcon(titleIcon, titleIconKey)"
          :src="resolveIcon(titleIcon, titleIconKey)"
          :alt="titleIconAlt || title || 'Card icon'"
          class="size-5 shrink-0"
          :class="{ 'invert-icon': !isLightMode && !titleIcon?.url }"
        />
        <div v-if="title" :class="[headingClasses, 'font-bold min-w-0']">
          <span class="block truncate">{{ title }}</span>
        </div>
      </div>
      <div
        v-if="cornerBadge?.label"
        class="shrink-0 inline-flex items-center gap-2 rounded-full px-3 py-1 text-[0.75rem] leading-[1rem] font-semibold"
        :class="isLightMode ? 'bg-[#F4EFE3] text-[#6B5744]' : 'bg-[#3C3A36] text-[#ECEBEB]'"
      >
        <img
          v-if="resolveIcon(cornerBadge.icon, cornerBadge.iconKey)"
          :src="resolveIcon(cornerBadge.icon, cornerBadge.iconKey)"
          :alt="cornerBadge.iconAlt || cornerBadge.label"
          class="size-4 shrink-0"
          :class="{ 'invert-icon': !isLightMode && !cornerBadge.icon?.url }"
        />
        <span>{{ cornerBadge.label }}</span>
      </div>
    </div>

    <div v-if="bodyBlocks.length" class="flex flex-col gap-y-4">
      <div
        v-for="(block, index) in bodyBlocks"
        :key="block._uid || block.id || index"
        v-bind="getEditableAttributes(block)"
      >
        <EventDateTimeBlock
          v-if="block.__component === 'hogc-one-event-page.date-time'"
          :event-name="block.eventName"
          :heading-size="block.headingSize"
          :date-time="block.dateTime"
        />
        <EventRichTextBlock
          v-else-if="block.__component === 'hogc-one-event-page.rich-text'"
          :title="block.title"
          :heading-size="block.headingSize"
          :body-text="block.bodyText"
        />
        <ImageCarousel
          v-else-if="block.__component === 'hogc-one-event-page.gallery'"
          :aspect-ratio="block.aspectRatio"
          :interaction-mode="block.interactionMode"
          :items="block.items"
          embedded
        />
        <EventActionButtons
          v-else-if="
            block.__component === 'hogc-one-event-page.action-button' && canShowAction(block)
          "
          :actions="[block]"
          @action="emitAction"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import { getStoryblokEditableAttributes } from '@/utils/storyblok-editable';
import { eventBus } from '@/utils/eventBus';
import { getHeadingClasses } from '@/utils/eventPageHeading';
import EventDateTimeBlock from '@/components/EventPage/EventDateTimeBlock.vue';
import EventRichTextBlock from '@/components/EventPage/EventRichTextBlock.vue';
import EventActionButtons from '@/components/EventPage/EventActionButtons.vue';
import ImageCarousel from '@/components/EventPage/ImageCarousel.vue';

const ICON_MAP = {
  calendar: '/assets/icons/event-page/calendar-icon.svg',
  info: '/assets/icons/event-page/info-icon.svg',
  map: '/assets/icons/event-page/location-icon.svg',
  ticket: '/assets/icons/event-page/ticket-icon.svg',
  users: '/assets/icons/event-page/profile-icon.svg',
};

export default {
  name: 'EventCard',
  components: {
    EventActionButtons,
    EventDateTimeBlock,
    EventRichTextBlock,
    ImageCarousel,
  },
  props: {
    title: {
      type: String,
      default: '',
    },
    headingSize: {
      type: String,
      default: 'H2',
    },
    titleIcon: {
      type: Object,
      default: null,
    },
    titleIconKey: {
      type: String,
      default: '',
    },
    titleIconAlt: {
      type: String,
      default: '',
    },
    cornerBadge: {
      type: Object,
      default: null,
    },
    bodyBlocks: {
      type: Array,
      default: () => [],
    },
  },
  computed: {
    ...mapState('eventPage', ['isLightMode']),
    ...mapState('profile', ['isLeader']),
    headingClasses() {
      return getHeadingClasses(this.headingSize);
    },
    hasHeader() {
      return Boolean(
        this.title ||
          this.resolveIcon(this.titleIcon, this.titleIconKey) ||
          this.cornerBadge?.label,
      );
    },
  },
  methods: {
    canShowAction(block) {
      if (block.__component !== 'hogc-one-event-page.action-button') return true;
      return !block.forLeaders || this.isLeader;
    },
    emitAction(action) {
      eventBus.emit('event-page-action', action);
    },
    getEditableAttributes(block) {
      return getStoryblokEditableAttributes(block);
    },
    resolveIcon(asset, key) {
      return asset?.url || ICON_MAP[key] || '';
    },
  },
};
</script>

<style scoped>
.invert-icon {
  filter: invert(100%) sepia(0%) saturate(0%) hue-rotate(147deg) brightness(105%) contrast(101%);
}
</style>
